import * as THREE from 'three';
import { ScrollController } from '../utils/ScrollController';

export class ScrollAnimatedObject {
  private group: THREE.Group;
  private particleSystem!: THREE.Points;
  private geometry!: THREE.BufferGeometry;
  private material!: THREE.PointsMaterial;
  private scrollController: ScrollController;
  private morphingGeometry: THREE.Mesh | null = null;
  private time: number = 0;
  private originalPositions!: Float32Array;

  constructor(scrollController: ScrollController) {
    this.scrollController = scrollController;
    this.group = new THREE.Group();
    this.setupParticleSystem();
    this.setupMorphingGeometry();
    this.setupScrollListener();
  }

  private setupParticleSystem(): void {
    const particleCount = 2000;
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);

    const color1 = new THREE.Color(0x90ee90); // Light green
    const color2 = new THREE.Color(0x228b22); // Forest green
    const color3 = new THREE.Color(0x32cd32); // Lime green

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;

      // Spherical distribution
      const radius = Math.random() * 3 + 1;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);

      positions[i3] = radius * Math.sin(phi) * Math.cos(theta);
      positions[i3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      positions[i3 + 2] = radius * Math.cos(phi);

      // Color variation
      const colorMix = Math.random();
      let color: THREE.Color;
      if (colorMix < 0.33) {
        color = color1.clone().lerp(color2, Math.random());
      } else if (colorMix < 0.66) {
        color = color2.clone().lerp(color3, Math.random());
      } else {
        color = color3.clone().lerp(color1, Math.random());
      }

      colors[i3] = color.r;
      colors[i3 + 1] = color.g;
      colors[i3 + 2] = color.b;

      sizes[i] = Math.random() * 0.05 + 0.02;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    this.material = new THREE.PointsMaterial({
      size: 0.1,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.particleSystem = new THREE.Points(this.geometry, this.material);
    this.group.add(this.particleSystem);
    
    // Store original positions
    this.originalPositions = new Float32Array(positions);
  }

  private setupMorphingGeometry(): void {
    // Create a morphing torus knot that transforms based on scroll
    const geometry = new THREE.TorusKnotGeometry(1, 0.3, 100, 16);
    
    const material = new THREE.MeshStandardMaterial({
      color: 0x90ee90,
      metalness: 0.7,
      roughness: 0.3,
      emissive: 0x228b22,
      emissiveIntensity: 0.2,
    });

    this.morphingGeometry = new THREE.Mesh(geometry, material);
    this.morphingGeometry.position.set(0, 0, 0);
    this.group.add(this.morphingGeometry);
  }

  private setupScrollListener(): void {
    this.scrollController.onScroll((progress) => {
      this.updateScrollAnimation(progress);
    });
  }

  private updateScrollAnimation(progress: number): void {
    // Animate particle system based on scroll
    const positions = this.geometry.attributes.position.array as Float32Array;

    // Create wave effect
    const waveIntensity = progress * 2;
    const expansion = 1 + progress * 1.5;

    for (let i = 0; i < positions.length; i += 3) {
      const x = this.originalPositions[i];
      const y = this.originalPositions[i + 1];
      const z = this.originalPositions[i + 2];

      const distance = Math.sqrt(x * x + y * y + z * z);
      const angle = Math.atan2(y, x);

      // Wave distortion
      const wave = Math.sin(distance * 2 + this.time * 2) * waveIntensity;
      
      positions[i] = x * expansion + Math.cos(angle) * wave * 0.3;
      positions[i + 1] = y * expansion + Math.sin(angle) * wave * 0.3;
      positions[i + 2] = z * expansion + Math.cos(distance) * wave * 0.3;
    }

    this.geometry.attributes.position.needsUpdate = true;

    // Animate morphing geometry
    if (this.morphingGeometry) {
      const rotationSpeed = 0.5 + progress * 2;
      const scale = 0.5 + progress * 1.5;
      
      this.morphingGeometry.rotation.x += 0.01 * rotationSpeed;
      this.morphingGeometry.rotation.y += 0.015 * rotationSpeed;
      this.morphingGeometry.scale.set(scale, scale, scale);

      // Update material based on scroll
      const material = this.morphingGeometry.material as THREE.MeshStandardMaterial;
      material.emissiveIntensity = 0.2 + progress * 0.5;
      material.opacity = 1 - progress * 0.7;
      material.transparent = progress > 0.3;
    }

    // Update particle material
    this.material.opacity = 0.8 - progress * 0.6;
    this.material.size = 0.1 + progress * 0.2;
  }

  public update(deltaTime: number): void {
    this.time += deltaTime;

    // Continuous rotation
    this.group.rotation.y += 0.002;

    // Particle animation
    const positions = this.geometry.attributes.position.array as Float32Array;
    const progress = this.scrollController.getScrollProgress();

    // Add subtle floating animation
    for (let i = 0; i < positions.length; i += 3) {
      positions[i + 1] += Math.sin(this.time + i * 0.01) * 0.0005 * (1 - progress);
    }

    this.geometry.attributes.position.needsUpdate = true;
  }

  public getGroup(): THREE.Group {
    return this.group;
  }

  public dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
    if (this.morphingGeometry) {
      this.morphingGeometry.geometry.dispose();
      (this.morphingGeometry.material as THREE.Material).dispose();
    }
  }
}
